// Small SVG chart primitives. Hand-rolled instead of pulling in a chart
// library — the analytics page needs three shapes (donut, horizontal
// bar, semicircle gauge), all trivially expressible in SVG, and the
// bundle stays 6 kB gzipped instead of ~40. Every chart accepts a
// simple `slices` / `bars` / `value` interface driven from the profit
// report data.

import type { CSSProperties, ReactNode } from 'react';

// ----- Donut chart ------------------------------------------------------

interface DonutSlice {
  label: string;
  value: number;
  className: string; // e.g. `currency--usd` → picks up the accent from tokens
}

interface DonutProps {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: string;
  ariaLabel: string;
}

export function Donut({
  slices,
  size = 200,
  thickness = 24,
  centerLabel,
  centerValue,
  ariaLabel,
}: DonutProps) {
  const positive = slices.filter((s) => s.value > 0);
  const total = positive.reduce((sum, s) => sum + s.value, 0);
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  if (total <= 0) {
    return (
      <svg width={size} height={size} role="img" aria-label={ariaLabel}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--hairline-strong)"
          strokeWidth={thickness}
        />
        {centerLabel ? (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--muted)"
            fontSize={14}
            fontFamily="var(--font-sans)"
          >
            {centerLabel}
          </text>
        ) : null}
      </svg>
    );
  }

  let offset = 0;
  return (
    <svg width={size} height={size} role="img" aria-label={ariaLabel}>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="var(--hairline-soft)"
        strokeWidth={thickness}
      />
      {positive.map((s) => {
        const len = (s.value / total) * circumference;
        const strokeDasharray = `${len} ${circumference - len}`;
        const strokeDashoffset = -offset;
        offset += len;
        return (
          <g key={s.label} className={s.className}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="var(--currency-accent, var(--ink))"
              strokeWidth={thickness}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          </g>
        );
      })}
      {centerValue ? (
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--ink)"
          fontSize={20}
          fontWeight={700}
          fontFamily="var(--font-mono)"
        >
          {centerValue}
        </text>
      ) : null}
      {centerLabel ? (
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--muted)"
          fontSize={11}
          fontFamily="var(--font-sans)"
          style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
        >
          {centerLabel}
        </text>
      ) : null}
    </svg>
  );
}

// ----- Horizontal bars --------------------------------------------------

interface HBar {
  label: string;
  value: number;
  displayValue: string;
  className: string;
}

interface HBarChartProps {
  bars: HBar[];
  ariaLabel: string;
}

export function HBars({ bars, ariaLabel }: HBarChartProps) {
  const max = Math.max(...bars.map((b) => Math.abs(b.value)), 1);
  return (
    <div className="hbar-list" role="list" aria-label={ariaLabel}>
      {bars.map((b) => {
        const pct = Math.max(2, Math.round((Math.abs(b.value) / max) * 100));
        return (
          <div key={b.label} className={`hbar ${b.className}`} role="listitem">
            <div className="hbar__label">{b.label}</div>
            <div className="hbar__track" aria-hidden="true">
              <div className="hbar__fill" style={{ inlineSize: `${pct}%` }} />
            </div>
            <div className="hbar__value">{b.displayValue}</div>
          </div>
        );
      })}
    </div>
  );
}

// ----- Semicircular gauge (net-profit vs target) -----------------------

interface GaugeProps {
  /** Numerator / denominator; renders capped at 1.0 but overshoots display separately. */
  value: number;
  max: number;
  centerValue: string;
  centerLabel: string;
  ariaLabel: string;
  className?: string;
}

export function Gauge({ value, max, centerValue, centerLabel, ariaLabel, className }: GaugeProps) {
  const size = 220;
  const thickness = 20;
  const cx = size / 2;
  const cy = size / 2 + 30;
  const r = (size - thickness) / 2;
  const halfCirc = Math.PI * r;

  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const filled = halfCirc * ratio;
  const strokeDasharray = `${filled} ${halfCirc - filled + halfCirc /* rest of full circle */}`;

  return (
    <svg
      width={size}
      height={size / 2 + 40}
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      {/* background track */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="var(--hairline-soft)"
        strokeWidth={thickness}
        strokeLinecap="round"
        strokeDasharray={`${halfCirc} ${halfCirc}`}
        transform={`rotate(180 ${cx} ${cy})`}
      />
      {/* fill */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="var(--currency-accent, var(--accent-green))"
        strokeWidth={thickness}
        strokeLinecap="round"
        strokeDasharray={strokeDasharray}
        transform={`rotate(180 ${cx} ${cy})`}
      />
      <text
        x={cx}
        y={cy - 14}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="var(--ink)"
        fontSize={26}
        fontWeight={700}
        fontFamily="var(--font-mono)"
      >
        {centerValue}
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="var(--muted)"
        fontSize={11}
        fontFamily="var(--font-sans)"
        style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        {centerLabel}
      </text>
    </svg>
  );
}

// ----- Legend chip strip (paired with Donut) ---------------------------

interface LegendItem {
  label: string;
  value: string;
  className: string;
}

export function Legend({ items, ariaLabel }: { items: LegendItem[]; ariaLabel: string }) {
  return (
    <ul className="chart-legend" aria-label={ariaLabel}>
      {items.map((it) => (
        <li key={it.label} className={`chart-legend__item ${it.className}`}>
          <span className="chart-legend__dot" aria-hidden="true" />
          <span className="chart-legend__label">{it.label}</span>
          <span className="chart-legend__value">{it.value}</span>
        </li>
      ))}
    </ul>
  );
}

// ----- Line series chart (daily time series, two series) ---------------

interface LinePoint {
  date: string;
  value: number;
}

interface LineSeriesData {
  label: string;
  points: LinePoint[];
  className: string;
}

interface LineChartProps {
  series: LineSeriesData[];
  ariaLabel: string;
}

export function LineSeries({ series, ariaLabel }: LineChartProps) {
  const W = 360;
  const H = 100;
  const PAD_T = 8;
  const PAD_B = 8;
  const ph = H - PAD_T - PAD_B;

  const allValues = series.flatMap((s) => s.points.map((p) => p.value));
  if (allValues.length === 0) return null;

  const minV = Math.min(...allValues);
  const maxV = Math.max(...allValues);
  const range = maxV === minV ? 1 : maxV - minV;

  function toCoords(points: LinePoint[]): string {
    const n = points.length;
    if (n === 0) return '';
    return points
      .map((p, i) => {
        const x = n === 1 ? W / 2 : (i / (n - 1)) * W;
        const y = PAD_T + ph * (1 - (p.value - minV) / range);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  const showDots = (series[0]?.points.length ?? 0) <= 10;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={ariaLabel}
      style={{ width: '100%', display: 'block' }}
    >
      {/* Zero baseline when range crosses zero */}
      {minV < 0 && maxV > 0 ? (
        <line
          x1={0}
          y1={PAD_T + ph * (1 - (0 - minV) / range)}
          x2={W}
          y2={PAD_T + ph * (1 - (0 - minV) / range)}
          stroke="var(--hairline-strong)"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
      ) : null}
      {series.map((s) => {
        const n = s.points.length;
        if (n === 0) return null;
        const coords = toCoords(s.points);
        return (
          <g key={s.label} className={s.className}>
            {n > 1 ? (
              <polyline
                points={coords}
                fill="none"
                stroke="var(--currency-accent, var(--ink))"
                strokeWidth={2.5}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ) : null}
            {showDots
              ? s.points.map((p, i) => {
                  const x = n === 1 ? W / 2 : (i / (n - 1)) * W;
                  const y = PAD_T + ph * (1 - (p.value - minV) / range);
                  return (
                    <circle
                      key={i}
                      cx={x.toFixed(1)}
                      cy={y.toFixed(1)}
                      r={n === 1 ? 5 : 3}
                      fill="var(--currency-accent, var(--ink))"
                    />
                  );
                })
              : null}
          </g>
        );
      })}
    </svg>
  );
}

// ----- Stacked horizontal bar (profit decomposition) -------------------

interface StackedSegment {
  label: string;
  value: number;
  className: string;
}

interface StackedBarProps {
  segments: StackedSegment[];
  ariaLabel: string;
}

export function StackedBar({ segments, ariaLabel }: StackedBarProps) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total <= 0) return null;
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'flex', width: '100%', height: 24, borderRadius: 6, overflow: 'hidden' }}
    >
      {segments.map((seg) => (
        <div
          key={seg.label}
          className={seg.className}
          style={{
            flex: seg.value / total,
            background: 'var(--currency-accent, var(--hairline-strong))',
            minWidth: seg.value > 0 ? 2 : 0,
          }}
          title={`${seg.label}: ${seg.value.toFixed(2)}`}
        />
      ))}
    </div>
  );
}

// ----- Wrapper card (shared frame around each chart) -------------------

export function ChartCard({
  title,
  action,
  children,
  style,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section className="chart-card" style={style}>
      <header className="chart-card__head">
        <h2 className="chart-card__title">{title}</h2>
        {action ?? null}
      </header>
      <div className="chart-card__body">{children}</div>
    </section>
  );
}
